import {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react'
import { errorObjectToString } from '../ApiClient'
import { AppToaster } from '../components/toaster'
import { useApi } from './ApiProvider'
import { useLocation, useParams } from 'react-router-dom'

/**
 * Context for the main window. Should contain all state and functions
 * that don't alter the state of the survey, but rather the state of the
 * UI, such as which dialog is open, which survey is currently selected,
 * etc.
 */
export const WindowContext = createContext()

export default function WindowProvider({ children }) {
  const [surveys, setSurveys] = useState()
  const [surveysPagination, setSurveysPagination] = useState({
    page: 1,
    per_page: 100,
    total_pages: 1,
    total_items: 0,
  })
  const [surveysSearch, setSurveysSearch] = useState('')
  const [currentSurvey, setCurrentSurvey] = useState()
  const [currentReports, setCurrentReports] = useState([])
  const [dialogOpen, setDialogOpen] = useState()
  const [settings, setSettings] = useState({
    statTesting: false,
    percentages: false,
    highlighting: false,
    showIndexTabs: true,
    showMeanTabs: true,
    surveysView: 'table',
    chart: null,
  })

  const api = useApi()
  const [banner, setBanner] = useState([])
  const [stub, setStub] = useState([])
  const [loading, setLoading] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatBannerIds, setChatBannerIds] = useState(null)
  const [chatStubIds, setChatStubIds] = useState(null)

  const { surveyId } = useParams()
  const location = useLocation()

  const fetchSurveys = useCallback(async (page = 1, per_page = 100, search = '') => {
    let url = `/surveys?page=${page}&per_page=${per_page}`
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    const response = await api.get(url)
    if (response.ok) {
      setSurveys(response.body.items)
      setSurveysPagination({
        page: response.body._meta.page,
        per_page: response.body._meta.per_page,
        total_pages: response.body._meta.total_pages,
        total_items: response.body._meta.total_items,
      })
    } else {
      setSurveys(null)
    }
  }, [api])

  useEffect(() => {
    if (location.pathname === '/') {
      fetchSurveys()
    }
  }, [location.pathname, fetchSurveys])

  const fetchReports = useCallback(async () => {
    if (!surveyId) return
    const response = await api.get(`/survey/${surveyId}/reports`)
    if (response.ok) {
      setCurrentReports(response.body)
    }
  }, [api, surveyId])

  const addSlideToReport = useCallback(
    async (slide, reportId) => {
      setLoading(true)
      const response = await api.post(`/report/${reportId}/slide`, slide)
      if (response.ok) {
        const reportIndex = currentReports.findIndex(
          r => r.id === reportId
        )
        setCurrentReports([
          ...currentReports.slice(0, reportIndex),
          response.body,
          ...currentReports.slice(reportIndex + 1),
        ])
      } else {
        const message = errorObjectToString(response.body.messages.json)
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
      setLoading(false)
    },
    [api, currentReports, setCurrentReports, setLoading]
  )

  const createReport = useCallback(
    async report => {
      setLoading(true)
      const response = await api.post(`/survey/${surveyId}/report`, report)
      if (response.ok) {
        setCurrentReports([...currentReports, response.body])
        return response.body
      } else {
        console.log(response)
        const message = errorObjectToString(response.body.messages.json)
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
      setLoading(false)
    },
    [api, currentReports, surveyId, setCurrentReports, setLoading]
  )

  const deleteSlide = useCallback(
    async (slideId, reportId) => {
      setLoading(true)
      const response = await api.delete(`/slide/${slideId}`)
      if (response.ok) {
        const reportIndex = currentReports.findIndex(
          r => r.id === reportId
        )
        setCurrentReports([
          ...currentReports.slice(0, reportIndex),
          {
            ...currentReports[reportIndex],
            slides: currentReports[reportIndex].slides.filter(
              s => s.id !== slideId
            ),
          },
          ...currentReports.slice(reportIndex + 1),
        ])
      } else {
        console.log(response)
        const message = `Error deleting (${response.status} - ${response?.body?.error})`
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
      setLoading(false)
    },
    [api, currentReports, setCurrentReports, setLoading]
  )

  const describeSegmentEmail = useCallback(
    async(slideId) => {
      setLoading(true)
      const response = await api.post(`/slide/${slideId}/describe`)
      if (response.ok) {
        AppToaster.show({
          message:
            "Task enqueued. You'll receive an email with the results when completed.",
          intent: 'success',
          icon: 'tick-circle',
        })
      } else {
        console.log(response)
        const message = `Error queuing describe segmentation (${response.status} - ${response?.body?.error})`
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
      setLoading(false)
    },
    [api, setLoading]
  )

  const describeSegment = useCallback(
    (bannerIds, stubIds) => {
      setChatBannerIds(bannerIds)
      setChatStubIds(stubIds)
      setIsChatOpen(true)
    },
    []
  )

  const renameSlide = useCallback(
    async (slideId, newName) => {
      setLoading(true)
      const response = await api.put(`/slide/${slideId}`, { name: newName })
      if (response.ok) {
        const newReportVersion = response.body
        const oldReports = currentReports
        setCurrentReports(oldReports.map(r => {
          if (r.id === newReportVersion.id) {
            return newReportVersion
          } else {
            return r
          }
        }))
        setLoading(false)
        return newReportVersion
      } else {
        console.log(response)
        const message = `Error renaming (${response.status} - ${response?.body?.error})`
        AppToaster.show({
          message,
          intent: 'danger',
          icon: 'error',
        })
      }
      setLoading(false)
    },
    [api, currentReports, setCurrentReports, setLoading]
  )

  return (
    <WindowContext.Provider
      value={{
        currentSurvey,
        setCurrentSurvey,
        currentReports,
        setCurrentReports,
        setDialogOpen,
        dialogOpen,
        surveys,
        setSurveys,
        surveysPagination,
        setSurveysPagination,
        surveysSearch,
        setSurveysSearch,
        fetchSurveys,
        settings,
        setSettings,
        banner,
        stub,
        setBanner,
        setStub,
        loading,
        setLoading,
        fetchReports,
        addSlideToReport,
        renameSlide,
        describeSegment,
        describeSegmentEmail,
        deleteSlide,
        createReport,
        isChatOpen,
        setIsChatOpen,
        chatBannerIds,
        chatStubIds,
      }}
    >
      {children}
    </WindowContext.Provider>
  )
}

export function useWindow() {
  const context = useContext(WindowContext)
  if (context === undefined) {
    throw new Error('useWindow must be used within a WindowProvider')
  }
  return context
}
